import { FollowType, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { FollowButtonSource } from "~/components/tracking/google-analytics/types";
import type { homeSectionLeadPrimaryContentItemContent$key } from "~/queries/__generated__/homeSectionLeadPrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Headline,
  LeadingImage,
  LinkHighlightArea,
  Primary,
  Rest,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledHomeFollowButton,
  SubHeadline,
  Summary,
  Topic,
  TopicContainer,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: homeSectionLeadPrimaryContentItemContent$key;
  variantProps?: ContentItemHomeSectionLeadPrimaryVariantProps;
} & ContentItemRenderVariantProps;

export type ContentItemHomeSectionLeadPrimaryVariantProps = {
  followButtonSource?: FollowButtonSource;
  withBookmark?: boolean;
  withComment?: boolean;
};

export const ContentItemHomeSectionLeadPrimary = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      className,
      customQueryParameters,
      onClick,
      provider,
      reference: reference_,
      variantProps = {
        withComment: false,
      },
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homeSectionLeadPrimaryContentItemContent on Content {
          entityId
          topics {
            ...followButtonBase
          }
          ...entityLink
          ...contentCoverImageContent
        }
      `,
      reference_,
    );

    const isSponsorArticle = notEmpty(provider.sponsorTag?.());
    const topic = first(content?.topics ?? []);

    return (
      <Container className={className} ref={reference}>
        <LeadingImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <StyledContentCoverImage
              reference={content}
              responsiveVariants={{
                desktopUp: "landscape250x99",
                homeDesktopUp: "landscape250x99",
                mobileUp: "size540x360",
                tabletUp: "landscape250x99",
              }}
            />
          </StyledEntityLink>
        </LeadingImage>
        <Primary>
          <TopicContainer>
            <Topic>{provider.topicLink?.()}</Topic>
            {topic && (
              <StyledHomeFollowButton
                buttonVariant="square"
                reference={topic}
                source={variantProps.followButtonSource}
                type={FollowType.Topic}
              />
            )}
          </TopicContainer>

          <StyledEntityLink
            query={customQueryParameters}
            reference={content}
            onClick={() => onClick?.(content.entityId)}
          >
            <LinkHighlightArea>
              <Headline>
                {provider.headline({
                  preferSocialHeadline: true,
                })}
              </Headline>
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
              <SubHeadline>{provider.subHeadline()}</SubHeadline>
            </LinkHighlightArea>
          </StyledEntityLink>

          {isSponsorArticle ? (
            provider.sponsorTag?.({ variant: "home" })
          ) : (
            <ActionBar>
              {provider.actionBar({
                isBookmarkVisible: variantProps?.withBookmark ?? false,
                isCommentsVisible: variantProps?.withComment ?? false,
              })}
            </ActionBar>
          )}
        </Primary>
        <Rest>{children}</Rest>
      </Container>
    );
  },
);

ContentItemHomeSectionLeadPrimary.displayName = "ContentItemHomeSectionLeadPrimary";
